import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, TextInput } from "@sia/style-guide";
import { MasterKeyContext } from "contexts/MasterKeyContext";
import masterKeyValidationAdapter from "common/Adapters/MasterKeyValidationAdapter/Router";
import { CRYPTO_TEST_MIGRATION_VERSIONS, disableCryptoTestMigration, isCryptoTestMigrationEnabled } from "common/crypto/testMigrationConfig";

const validKey = (value) => value.length >= 5 && value.length <= 15;

const otherVersion = (version) => CRYPTO_TEST_MIGRATION_VERSIONS.find((candidate) => candidate !== version) || "v2";

export default function CryptoMigrationTestPanel() {
  const { isMasterKeyValid, cryptoEngineVersion } = useContext(MasterKeyContext);
  const [qaEnabled, setQaEnabled] = useState(() => isCryptoTestMigrationEnabled());
  const [oldKey, setOldKey] = useState("");
  const [newKey, setNewKey] = useState("");
  const [targetVersion, setTargetVersion] = useState(() => otherVersion(cryptoEngineVersion));
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setQaEnabled(isCryptoTestMigrationEnabled());
  }, []);

  useEffect(() => {
    setTargetVersion(otherVersion(cryptoEngineVersion));
    setMessage("");
  }, [cryptoEngineVersion]);

  const effectiveNewKey = useMemo(() => newKey || oldKey, [newKey, oldKey]);

  if (!qaEnabled || !isMasterKeyValid) return null;

  const migrate = async () => {
    setMessage("");

    if (!validKey(oldKey)) return setMessage("Current master key must contain 5–15 characters.");
    if (!validKey(effectiveNewKey)) return setMessage("Target master key must contain 5–15 characters.");
    if (targetVersion === cryptoEngineVersion && oldKey === effectiveNewKey)
      return setMessage(`The account is already using ${targetVersion.toUpperCase()} with this master key.`);
    if (!window.confirm(`[QA only] Migrate ${cryptoEngineVersion?.toUpperCase()} → ${targetVersion.toUpperCase()}?`)) return;

    setBusy(true);

    try {
      const response = await masterKeyValidationAdapter.migrateForTesting(oldKey, effectiveNewKey, targetVersion);
      const version = response?.data?.cryptoVersion || targetVersion;
      setOldKey("");
      setNewKey("");
      setMessage(`Migration completed. Active engine: ${String(version).toUpperCase()}.`);
    } catch (error) {
      console.error("QA crypto migration failed", error);
      setMessage(error?.message || "QA crypto migration failed.");
    } finally {
      setBusy(false);
    }
  };

  const disableQaMode = () => {
    disableCryptoTestMigration();
    setQaEnabled(false);
  };

  return (
    <details
      style={{
        margin: "16px 0",
        padding: 12,
        border: "1px dashed #6f6f6f",
      }}
    >
      <summary
        style={{
          cursor: "pointer",
          fontWeight: 600,
        }}
      >
        [QA only] Crypto migration testing
      </summary>

      <div
        style={{
          marginTop: 12,
          display: "grid",
          gap: 12,
          maxWidth: 520,
        }}
      >
        <div>
          Current engine: <strong>{String(cryptoEngineVersion || "unknown").toUpperCase()}</strong>
        </div>

        <label htmlFor="qa-crypto-target-version">
          Target engine
          <select
            id="qa-crypto-target-version"
            value={targetVersion}
            disabled={busy}
            onChange={(event) => setTargetVersion(event.target.value)}
            style={{
              display: "block",
              marginTop: 4,
              minWidth: 160,
            }}
          >
            {CRYPTO_TEST_MIGRATION_VERSIONS.map((version) => (
              <option key={version} value={version}>
                {version.toUpperCase()}
              </option>
            ))}
          </select>
        </label>

        <TextInput
          id="qa-crypto-current-master-key"
          label="Current master key"
          password
          value={oldKey}
          disabled={busy}
          onChange={(event) => setOldKey(event.target.value)}
        />

        <TextInput
          id="qa-crypto-target-master-key"
          label="Target master key (leave empty to keep the same key)"
          password
          value={newKey}
          disabled={busy}
          onChange={(event) => setNewKey(event.target.value)}
        />

        <div
          style={{
            display: "flex",
            gap: 8,
          }}
        >
          <Button kind="primary" disabled={busy} onClick={migrate}>
            {busy ? "Migrating…" : `Migrate to ${targetVersion.toUpperCase()}`}
          </Button>

          <Button kind="secondary" disabled={busy} onClick={disableQaMode}>
            Exit QA mode
          </Button>
        </div>

        {message && <div role="status">{message}</div>}

        <small>
          QA mode is enabled only for this browser session. Open this Survey URL with <code>?cryptoMigrationTest=1</code> to enable it again.
        </small>
      </div>
    </details>
  );
}
